import React, { ElementType, FC, PropsWithChildren, ReactNode } from 'react'

import { css, cx, LinariaClassName } from '@linaria/core'
import { styled } from '@linaria/react'
import { Link } from 'react-router-dom'

import { VoidHandler, WithClassName, WithDataName } from 'common/types'
import {
  Props,
  Typography,
} from 'components/designSystem/Typography/Typography'

export const MenuIcon: FC<
  {
    onClick?: VoidHandler
    icon?: ReactNode | string
    iconType?: 'react' | 'image'
    iconStyle?: LinariaClassName
    textStyle?: LinariaClassName
    text: ReactNode
    tag: ElementType
    href?: string
    externalHref?: boolean
    hasBackground?: boolean
  } & Props &
    WithClassName &
    WithDataName
> = ({
  className,
  onClick,
  icon,
  iconType = 'react',
  iconStyle,
  textStyle,
  text,
  tag = 'div',
  href = '',
  externalHref = false,
  hasBackground = false,
  fontSize,
  fontWeight,
  ...rest
}) => {
  const _icon = iconType === 'image' ? <Image src={icon as string} /> : icon

  return (
    <Parent className={cx(className)} onClick={onClick} as={tag}>
      <LinkDetector href={href} externalHref={externalHref}>
        {icon && (
          <div className={cx(iconStyle, hasBackground && iconBackgroundCss)}>
            {_icon}
          </div>
        )}
        <Text
          fontSize={fontSize}
          fontWeight={fontWeight}
          tag="p"
          className={cx(textStyle)}
          data-name={rest['data-name']}
        >
          {text}
        </Text>
      </LinkDetector>
    </Parent>
  )
}

const LinkDetector: FC<
  PropsWithChildren & {
    href?: string
    externalHref?: boolean
  }
> = ({ children, href, externalHref }) => {
  if (href) {
    if (externalHref) {
      return (
        <a href={href} className={cx(linkCss)} target="_blank" rel="noreferrer">
          {children}
        </a>
      )
    }

    return (
      <Link to={href} className={cx(linkCss)}>
        {children}
      </Link>
    )
  }

  return children
}

const Parent = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--spacing-12px);
  grid-gap: var(--spacing-12px);
  justify-content: flex-start;
  align-items: center;
`

const iconBackgroundCss = css`
  padding: var(--spacing-8px);
  border-radius: 80px;
  background-color: var(--warm-hard);
  color: var(--warm-on-warm);
  box-sizing: content-box;
`

const Image = styled.img`
  display: block;
`

const Text = styled(Typography)`
  position: relative;
  top: 1px;
`

const linkCss = css`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--spacing-12px);
  grid-gap: var(--spacing-12px);
  justify-content: flex-start;
  align-items: center;
  text-decoration: none;

  &:hover,
  &:active {
    text-decoration: none;
  }
`
